import React, { useState, useEffect, useCallback, useMemo, useRef, RefObject } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCarsDiffThanEntrega, searchCars, filterCarsNonEntrega } from '../../../api/carsApi';
import { CarClass } from '../../../interfaces/Car';
import { RootState } from '../../../store/store';
import {
  setSelectedClasses,
  setSelectedStatuses,
  setSelectedPendingStatuses,
  setSelectedReleaseStatus,
  resetFilters,
  setSearchResults,
  setReduxCurrentPage,
  setLastSearchQuery,
  resetSearchResults,
  setSortOrderRedux
} from '../administracaoReducer';
import { formatReleaseDate } from '../../../utils/releaseDate';
import FilterCarModal from '../../../components/modal/filter-car/FilterCarModal';
import AdministracaoActionsModal from '../../../components/modal/administracao-actions-modal/AdministracaoActionsModal';
import ArrowLeftOrange from '../../../img/arrow-left-orange.svg';
import ArrowLeftGray from '../../../img/arrow-left-gray.svg';
import ArrowRightOrange from '../../../img/arrow-right-orange.svg';
import ArrowRightGray from '../../../img/arrow-right-gray.svg';
import ArrowDown from '../../../img/arrow-down.svg';
import CarAiro from '../../../img/car-airo.svg';
import CarAssociado from '../../../img/car-associado.svg';
import CarAtual from '../../../img/car-atual.svg';
import CarMobi from '../../../img/car-mobi.svg';
import CarPerdaTotal from '../../../img/car-perda-total.svg';
import CarTerceiro from '../../../img/car-terceiro.svg';
import CarExterno from '../../../img/car-externo.svg';
import CarDiretoria from '../../../img/car-diretoria.svg';
import Moto from '../../../img/moto.svg';
import SearchIcon from '../../../img/search-icon.svg';
import Filter from '../../../img/filter.svg';
import FullScreenLoader from '../../../components/loading/FullScreenLoader';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { useTableColumns } from '../../../hooks/useTableColumns';
import { useGuardedReload } from '../../../hooks/useGuardedReload';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import '../../../components/como-funciona/help-circle-btn.css';

// Reuse recepcao styles to keep page visuals perfectly uniform
import '../../recepcao/pages/Recepcao.css';
import '../../../shared/feedback/erro-de-tela.css';

interface AdministracaoProps {
  sidebarRef?: RefObject<HTMLDivElement>;
}

interface FilterParams {
  carClass?: string | string[];
  carStatus?: string | string[];
  pendingStatus?: string | string[];
  released?: boolean;
  releaseStatus?: string[];
}

const DEFAULT_SORT_BY = 'entryDate';
const DEFAULT_SORT_ORDER: 'asc' | 'desc' = 'desc';

const defaultColumnWidths = [
  110, 160, 140, 150, 160, 130
];

const Administracao: React.FC<AdministracaoProps> = ({ sidebarRef }) => {
  const dispatch = useDispatch();
  const { labelDe } = useEsteiras(true);

  const [cars, setCars] = useState<any[]>([]);
  const [totalCars, setTotalCars] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>(DEFAULT_SORT_BY);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(DEFAULT_SORT_ORDER);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [isDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const [actionsModalOpen, setActionsModalOpen] = useState<boolean>(false);
  const [selectedCarForActions, setSelectedCarForActions] = useState<string | null>(null);
  const [isStatusSidebarOpen] = useState<boolean>(true);

  const availableClasses: CarClass[] = Object.values(CarClass);

  const startCarIndex = totalCars === 0 ? 0 : (currentPage - 1) * limit + 1;
  const endCarIndex = Math.min(currentPage * limit, totalCars);

  const getCarClassIcon = (carClass?: string) => {
    if (carClass === 'TERCEIRO' || carClass === 'Terceiro') {
      return <img src={CarTerceiro} alt="Terceiro" width="15" height="15" />;
    }
    if (carClass === 'ASSOCIADO' || carClass === 'Associado') {
      return <img src={CarAssociado} alt="Associado" width="15" height="15" />;
    }
    if (carClass === 'MOBI' || carClass === 'Mobi') {
      return <img src={CarMobi} alt="Mobi" width="15" height="15" />;
    }
    if (carClass === 'CARROS_EXTERNOS' || carClass === 'Carros Externos') {
      return <img src={CarExterno} alt="Carros Externos" width="15" height="15" />;
    }
    if (carClass === 'CARROS_ATUAL' || carClass === 'Carros Atual') {
      return <img src={CarAtual} alt="Carros Atual" width="15" height="15" />;
    }
    if (carClass === 'MOTO' || carClass === 'Moto') {
      return <img src={Moto} alt="Moto" width="15" height="15" />;
    }
    if (carClass === 'AIRO_CLUBE' || carClass === 'Airo Clube') {
      return <img src={CarAiro} alt="Airo Clube" width="15" height="15" />;
    }
    if (carClass === 'PERDA_TOTAL' || carClass === 'Perda Total') {
      return <img src={CarPerdaTotal} alt="Perda Total" width="15" height="15" />;
    }
    if (carClass === 'DIRETORIA' || carClass === 'Diretoria') {
      return <img src={CarDiretoria} alt="Diretoria" width="15" height="15" />;
    }
    return null;
  };

  const loadCars = async (page: number, limit: number, sortBy: string = DEFAULT_SORT_BY, sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetchCarsDiffThanEntrega(page, limit, sortBy, sortOrder);
      const filteredTotal = response.total;
      setCars(response.cars);
      setTotalCars(filteredTotal);
      setTotalPages(Math.ceil(filteredTotal / limit));
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (
    page: number = 1,
    sortBy: string = DEFAULT_SORT_BY,
    sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    setError(null);
    handleResetFilters();
    try {
      const response = await searchCars(
        searchQuery,
        page,
        limit,
        sortBy,
        sortOrder
      );

      dispatch(
        setSearchResults({
          page,
          cars: response.cars,
          totalItems: response.total,
          totalPages: Math.ceil(response.total / limit),
        })
      );
      dispatch(setReduxCurrentPage(page));
      dispatch(setLastSearchQuery(searchQuery));

      setCars(response.cars);
      setTotalCars(response.total);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (err) {
      setError('Erro ao realizar a pesquisa');
    } finally {
      setLoading(false);
    }
  };

  const openFilterModal = () => {
    setFilterModalOpen(true);
  };

  const handleApplyFilters = async (filters: any) => {
    dispatch(resetSearchResults());
    dispatch(setSelectedClasses(filters.carClass || []));
    dispatch(setSelectedStatuses(filters.carStatus || []));
    dispatch(setSelectedPendingStatuses(filters.pendingStatus || []));
    dispatch(setSelectedReleaseStatus(filters.releaseStatus || []));
    setCurrentPage(1);

    setLoading(true);
    setError(null);
    try {
      const response = await filterCarsNonEntrega(
        filters.carClass,
        filters.carStatus,
        filters.pendingStatus,
        undefined,
        undefined,
        1,
        limit,
        sortBy ?? DEFAULT_SORT_BY,
        sortOrder ?? DEFAULT_SORT_ORDER,
        filters.releaseStatus
      );

      setCars(response.cars);
      setTotalCars(response.total);
      setTotalPages(Math.ceil(response.total / limit));
    } catch (err) {
      setError('Erro ao aplicar os filtros');
    } finally {
      setLoading(false);
    }

    setFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const selectedClasses = useSelector((state: RootState) => state.administracao.selectedClasses);
  const selectedStatuses = useSelector((state: RootState) => state.administracao.selectedStatuses);
  const selectedPendingStatuses = useSelector((state: RootState) => state.administracao.selectedPendingStatuses);
  const selectedReleaseStatus = useSelector((state: RootState) => state.administracao.selectedReleaseStatus);

  const appliedFilters = {
    carClass: selectedClasses || [],
    carStatus: selectedStatuses || [],
    pendingStatus: selectedPendingStatuses || [],
    releaseStatus: selectedReleaseStatus || [],
  };

  /** Há filtro em vigor? (usado pelo `refreshList` para não perder o filtro — at-65) */
  const hasAnyFilter = (filters: typeof appliedFilters) =>
    Object.values(filters).some((val) => (Array.isArray(val) ? val.length > 0 : Boolean(val)));

  const loadCarsWithFilters = async (
    filters: FilterParams,
    page: number,
    limit: number,
    sortBy: string = DEFAULT_SORT_BY,
    sortOrder: 'asc' | 'desc' = DEFAULT_SORT_ORDER
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await filterCarsNonEntrega(
        filters.carClass,
        filters.carStatus,
        filters.pendingStatus,
        undefined,
        undefined,
        page,
        limit,
        sortBy,
        sortOrder,
        filters.releaseStatus
      );

      const filteredTotal = response.total;
      setCars(response.cars);
      setTotalCars(filteredTotal);
      setTotalPages(Math.ceil(filteredTotal / limit));
    } catch (err) {
      setError('Erro ao carregar os carros');
    } finally {
      setLoading(false);
    }
  };

  const handleSortChange = (selectedSortBy: string, order: 'asc' | 'desc') => {
    dispatch(setSortOrderRedux({ sortBy: selectedSortBy, sortOrder: order }));
    setSortBy(selectedSortBy);
    setSortOrder(order);
    setIsFilterDropdownOpen(false);
  };

  const handleClickOutsideFilterDropdown = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsFilterDropdownOpen(false);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutsideFilterDropdown);
    } else {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutsideFilterDropdown);
    };
  }, [isDropdownOpen]);

  // Recarrega a lista só quando filtros/página/limite/ordenação mudam de fato — a comparação
  // com o estado anterior vive no useGuardedReload (at-43); a decisão de COMO recarregar
  // (busca por texto × filtros × listagem) continua aqui.
  useGuardedReload({
    filters: appliedFilters,
    page: currentPage,
    limit,
    sortBy,
    sortOrder,
    searchQuery,
    onReload: ({ hasSortChanged }) => {
      setLoading(true);

      const someValidFilter =
        appliedFilters.carClass.length > 0 ||
        appliedFilters.carStatus.length > 0 ||
        appliedFilters.pendingStatus.length > 0 ||
        appliedFilters.releaseStatus.length > 0;

      const safeSortBy = sortBy ?? DEFAULT_SORT_BY;
      const safeSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;

      if (searchQuery) {
        handleSearch(currentPage, safeSortBy, safeSortOrder);
      } else if (someValidFilter || hasSortChanged) {
        loadCarsWithFilters(
          appliedFilters,
          currentPage,
          limit,
          safeSortBy,
          safeSortOrder
        );
      } else {
        loadCars(currentPage, limit, safeSortBy, safeSortOrder);
      }
    },
    onSkip: () => setLoading(false),
  });

  const headers = [
    'Placa',
    'Modelo',
    'Classe',
    'Esteira atual',
    'Esteira pendente',
    'Liberação Adm'
  ];

  const tableScrollRef = useRef<HTMLDivElement>(null);
  const headerRefs = useRef<(HTMLDivElement | null)[]>([]);

  const [isHelpOpen, setIsHelpOpen] = useState(false);

  const helpSections: ComoFuncionaSecao[] = [
    {
      titulo: 'O que esta tela decide',
      conteudo: (
        <>
          <p>
            Aqui a Administração autoriza cada veículo a entrar nas esteiras de trabalho. Enquanto uma
            esteira não estiver liberada para o veículo, ele <strong>não entra nela</strong>, não recebe
            colaborador responsável e ninguém consegue iniciar atividade. É o freio usado quando algo
            ainda pende do lado administrativo — documentação, pagamento, análise.
          </p>
          <p>
            O veículo que voltou em <strong>retorno de garantia</strong> aparece marcado com o selo{' '}
            <strong>Pós-evento</strong> ao lado da placa. Ele é um atendimento como qualquer outro e{' '}
            <strong>também precisa de liberação</strong> — não há exceção: é assim que a Administração fica
            sabendo de cada retrabalho.
          </p>
        </>
      ),
    },
    {
      titulo: 'Liberação total e liberação por esteira',
      conteudo: (
        <>
          <p>
            A <strong>liberação total</strong> vale para todas as esteiras de trabalho, inclusive as
            que forem criadas depois. A <strong>liberação por esteira</strong> autoriza somente as
            esteiras marcadas, uma a uma.
          </p>
          <p>
            As duas são independentes: remover a total não apaga as liberações por esteira que você
            marcou. A coluna <strong>Liberação Adm</strong> mostra o estado de cada veículo —
            <strong> Total</strong>, <strong>Parcial</strong> (com a quantidade de esteiras) ou
            <strong> Pendente</strong>.
          </p>
        </>
      ),
    },
    {
      titulo: 'Esteiras que não precisam de liberação',
      conteudo: (
        <p>
          As esteiras marcadas como <strong>de sistema</strong> fluem sem liberação e por isso não aparecem
          para marcar. Fazem parte disso as etapas do ciclo de vida do veículo — Aguardando, Aguardando
          próxima etapa, Orçamento, Aguardando entrega e Entrega —, o que garante que o checklist de entrada
          e a devolução do veículo ao cliente nunca fiquem travados. A Administração do Sistema pode marcar
          outras etapas como de sistema, e elas passam a fluir livres do mesmo jeito.
        </p>
      ),
    },
    {
      titulo: 'Ao remover uma liberação',
      conteudo: (
        <>
          <p>
            Remover exige confirmação e aceita um motivo, que fica registrado. A partir daí o veículo
            não entra mais nas esteiras desmarcadas nem recebe novo responsável nelas.
          </p>
          <p>
            Se o veículo <strong>estiver na esteira que perdeu a liberação</strong> e houver atividade
            em andamento ou pausada, ela é <strong>encerrada automaticamente</strong>: as horas já
            trabalhadas são preservadas e seguem para a aprovação do Chefe de Oficina, e o colaborador
            recebe um aviso. A tela de confirmação avisa antes, quando for o caso.
          </p>
          <p>
            O serviço daquela esteira fica registrado como <strong>inacabado</strong> — não se perde:
            quando a liberação voltar e o veículo retornar à esteira, o trabalho é retomado de onde
            parou.
          </p>
          <p>
            Remover a liberação <strong>não move</strong> o veículo de esteira — ele continua onde
            está, apenas sem poder trabalhar.
          </p>
        </>
      ),
    },
    {
      titulo: 'Registro e avisos',
      conteudo: (
        <p>
          Toda concessão e toda remoção ficam na Auditoria do veículo, com quem fez, quando e o que
          mudou. A cada nova liberação concedida, os Chefes de Oficina são avisados. Veículo já
          entregue não aceita alteração de liberação, porque o atendimento foi encerrado.
        </p>
      ),
    },
  ];

  // Valores renderizados por linha, memoizados para o hook de medição (at-43).
  // `labelDe` é estável (useCallback no useEsteiras), então as dependências fecham.
  const getColumnValues = useCallback(
    (car: any) => [
      car.plate || '-',
      car.model || '-',
      car.carClass || '-',
      labelDe(car.status),
      labelDe(car.pendingStatus),
      (car.releaseStatus === 'TOTAL' || (!car.releaseStatus && car.releasedAt))
        ? 'Total'
        : car.releaseStatus === 'PARCIAL'
        ? `Parcial (${(car.esteirasLiberadas || []).length})`
        : 'Pendente',
    ],
    [labelDe],
  );

  const tableRows = useMemo(() => cars.map(getColumnValues), [cars, getColumnValues]);

  const { tableColumns, tableWidth } = useTableColumns({
    containerRef: tableScrollRef,
    headerCellsRef: headerRefs,
    headers,
    defaultWidths: defaultColumnWidths,
    rows: tableRows,
  });

  const handleOpenActionsModal = (carId: string) => {
    setSelectedCarForActions(carId);
    setActionsModalOpen(true);
  };

  const handleCloseActionsModal = () => {
    setActionsModalOpen(false);
    setSelectedCarForActions(null);
  };

  /**
   * Recarrega a lista preservando o que está em vigor (at-65, BUG-V40).
   *
   * Antes caía direto no `loadCars` (listagem sem filtro) quando não havia busca, então
   * salvar uma liberação fazia a lista "pular" para a listagem completa — justo no fluxo
   * mais comum da tela: filtrar por "Pendente", liberar, seguir a fila. O usuário perdia
   * o lugar e precisava refiltrar a cada veículo.
   */
  const refreshList = () => {
    const safeSortBy = sortBy ?? DEFAULT_SORT_BY;
    const safeSortOrder = sortOrder ?? DEFAULT_SORT_ORDER;
    if (searchQuery) {
      void handleSearch(currentPage, safeSortBy, safeSortOrder);
      return;
    }

    if (hasAnyFilter(appliedFilters)) {
      void loadCarsWithFilters(appliedFilters, currentPage, limit, safeSortBy, safeSortOrder);
      return;
    }

    void loadCars(currentPage, limit, safeSortBy, safeSortOrder);
  };

  return (
    <div
      className="Frame1000004012"
      style={{
        height: loading ? '100vh' : 'fit-content',
        width: '100%',
        display: 'flex',
        justifyContent: loading ? 'center' : 'space-between',
        alignItems: loading ? 'center' : 'flex-start',
        backgroundColor: loading ? '#808080' : 'transparent',
      }}
    >
      {loading && <FullScreenLoader />}
      {!loading && (
        <div className="ConteDo recepcao-conte-do">
          {/* at-65: a faixa fica DENTRO do conteúdo e a tela continua de pé — antes o
              `!error` apagava cabeçalho, busca, filtros e tabela de uma vez (BUG-V27). */}
          {error && (
            <div className="erro-de-tela" role="alert">
              {error}
            </div>
          )}
          <div className="recepcao-header-row">
            <div className="recepcao-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              Administração
              <button
                type="button"
                className="help-circle-btn"
                title="Como Funciona"
                onClick={() => setIsHelpOpen(true)}
              >
                ?
              </button>
            </div>
            <div className="recepcao-search-bar">
              <div className="ItemSearch" onClick={() => void handleSearch()}>
                <img
                  className="imgIcon"
                  src={SearchIcon}
                  alt="Ícone de busca"
                  width="20"
                  height="20"
                />
              </div>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
                placeholder="Pesquise por placa, responsável ou nº do evento"
                className="recepcao-search-input"
              />
            </div>
          </div>
          <div className="VisualizarOrdenar">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <div className="CtaBig">
                <div className="Frame1000003987">
                  <div className="ComponentName">Itens por página:</div>
                  <select
                    id="limit"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="ComponentNameImgIcon"
                    style={{ cursor: 'pointer' }}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              </div>
              <div className="CtaBig MouseHover" onClick={openFilterModal}>
                <div className="Frame1000003987">
                  <img
                    src={Filter}
                    alt="Filtro"
                    width="19.80"
                    height="19.80"
                  />
                  <div className="ComponentName">Filtrar</div>
                </div>
              </div>
              <div className="CtaBig MouseHover" onClick={() => setIsFilterDropdownOpen(!isDropdownOpen)}>
                <div className="Frame1000003987">
                  <img
                    src={ArrowDown}
                    alt="Filtro"
                    width="12.80"
                    height="12.80"
                  />
                  <div className="ComponentName">Ordenar por</div>
                </div>
 
                {isDropdownOpen && (
                  <div className="DropdownMenu" ref={dropdownRef} onClick={(e) => e.stopPropagation()}>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('plate', 'asc');
                      }}
                    >
                      Placa
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'desc');
                      }}
                    >
                      Data de entrada mais recente
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('entryDate', 'asc');
                      }}
                    >
                      Data de entrada mais antiga
                    </div>
                    <div
                      className="DropdownOption"
                      onClick={() => {
                        setIsFilterDropdownOpen(false);
                        handleSortChange('releasedAt', 'desc');
                      }}
                    >
                      Liberação mais recente
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
 
          <div className="QuantidadeDeCarros">
            <div className="Frame1000004037" style={{ alignSelf: 'stretch', justifyContent: 'flex-start', alignItems: 'center', gap: 12, display: 'flex', flexWrap: 'wrap' }}>
              <div className="CtaQuantidadeDeCarros">
                <div className="Frame1000003987">
                  <div className="ComponentName">
                    {totalCars === 0
                      ? '0 carros'
                      : `${startCarIndex}-${endCarIndex} de ${totalCars} carros`}
                  </div>
                </div>
              </div>
            </div>
          </div>
 
          <div style={{ width: '100%', paddingTop: 30, background: 'var(--tema-superficie, white)', borderRadius: 16, border: '1px var(--tema-linha-2, #CACACA) solid', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', display: 'flex', marginBottom: 20 }}>
            <div className="TabelaScroll" ref={tableScrollRef}>
              <div
                className="TabelaContent"
                style={{
                  ['--table-columns' as any]: tableColumns,
                  width: tableWidth,
                  minWidth: tableWidth,
                }}
              >
                <div className="Tabela TabelaHeader">
                  <div className="Placa">
                    <div className="TTulo">
                      {headers.map((header, index) => (
                        <div
                          key={header}
                          className="Opcoes"
                          ref={(el) => {
                            headerRefs.current[index] = el;
                          }}
                        >
                          {header}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {cars.length === 0 ? (
                  <div className="Tabela">
                    <div className="Item" style={{ gridColumn: `1 / span ${headers.length}` }}>
                      <div className="Data">Nenhum carro encontrado.</div>
                    </div>
                  </div>
                ) : (
                  cars.map((car) => {
                    return (
                      <div key={car.id} className="Tabela">
                        <div className="Item">
                          <div
                            className="Data"
                            style={{ cursor: 'pointer', color: '#ea5d0e', textDecoration: 'underline' }}
                            onClick={() => handleOpenActionsModal(car.id)}
                          >
                            {car.plate || '-'}
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{car.model || '-'}</div>
                        </div>

                        <div className="Item">
                          <div className="Data" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {getCarClassIcon(car.carClass)}
                            <span>{car.carClass || '-'}</span>
                          </div>
                        </div>

                        <div className="Item">
                          <div className="Data">{labelDe(car.status)}</div>
                        </div>

                        <div className="Item">
                          <div className="Data">{labelDe(car.pendingStatus)}</div>
                        </div>

                        <div className="Item">
                          <div className="Data">
                            {(car.releaseStatus === 'TOTAL' || (!car.releaseStatus && car.releasedAt)) ? (
                              <span
                                className="badge-liberado"
                                style={{
                                  backgroundColor: 'var(--tema-fundo-2, #e6f4ea)',
                                  color: 'var(--tema-texto-verde, #137333)',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                }}
                                title={`Liberado por: ${car.releasedBy?.fullName || '-'}\nData: ${formatReleaseDate(car.releasedAt)}`}
                              >
                                Total
                              </span>
                            ) : car.releaseStatus === 'PARCIAL' ? (
                              <span
                                className="badge-parcial"
                                style={{
                                  backgroundColor: 'var(--tema-tinta-azul, #e0f2fe)',
                                  color: 'var(--tema-texto-azul, #0369a1)',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                  cursor: 'pointer',
                                }}
                                title={`Esteiras liberadas:\n${(car.esteirasLiberadas || []).map((e: any) => e.nome).join(', ')}`}
                              >
                                Parcial ({(car.esteirasLiberadas || []).length})
                              </span>
                            ) : (
                              <span
                                className="badge-pendente"
                                style={{
                                  backgroundColor: 'var(--tema-fundo, #f1f3f4)',
                                  color: 'var(--tema-texto-2, #5f6368)',
                                  padding: '4px 8px',
                                  borderRadius: '12px',
                                  fontSize: '11px',
                                  fontWeight: 500,
                                }}
                              >
                                Pendente
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            <div className="PaginaO">
              <div
                className={`Frame1000003950 ${currentPage === 1 ? 'disabled' : ''}`}
                onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
                style={{ cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
              >
                <img
                  className="imgIcon"
                  src={currentPage === 1 ? ArrowLeftGray : ArrowLeftOrange}
                  alt="Seta para Esquerda"
                  width="24"
                  height="24"
                />
                <div className="Anterior" style={{ color: currentPage === 1 ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Anterior
                </div>
              </div>
              <div className="Group21">
                <div className="Group20">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <div
                      key={p}
                      className={`page-number ${currentPage === p ? 'active' : ''}`}
                      onClick={() => setCurrentPage(p)}
                    >
                      {p}
                      {currentPage === p && <div className="Ellipse14" />}
                    </div>
                  ))}
                </div>
              </div>
              <div
                className={`Frame1000003949 ${currentPage === totalPages ? 'disabled' : ''}`}
                onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
                style={{ cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
              >
                <div className="PrXima" style={{ color: currentPage === totalPages ? 'var(--tema-texto-4, #999)' : 'var(--tema-texto, #1A1C1B)' }}>
                  Próxima
                </div>
                <img
                  className="imgIcon"
                  src={currentPage === totalPages ? ArrowRightGray : ArrowRightOrange}
                  alt="Seta para Direita"
                  width="24"
                  height="24"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* at-65 (BUG-V43): montagem condicional + filtros em vigor. */}
      {filterModalOpen && (
        <FilterCarModal
          onCancel={() => setFilterModalOpen(false)}
          onApplyFilters={handleApplyFilters}
          availableClasses={availableClasses}
          sidebarRef={sidebarRef}
          isSidebarOpen={isStatusSidebarOpen}
          showReleaseFilter
          onResetFilters={handleResetFilters}
          filtrosAplicados={appliedFilters}
        />
      )}

      {actionsModalOpen && selectedCarForActions && (
        <AdministracaoActionsModal
          isOpen={actionsModalOpen}
          carId={selectedCarForActions}
          onCancel={handleCloseActionsModal}
          onCarUpdated={refreshList}
          sidebarRef={sidebarRef}
        />
      )}

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona — Controle de Liberação"
        secoes={helpSections}
      />
    </div>
  );
};

export default Administracao;
